import {ErrorResponse} from "./types";

export class HTTPError extends Error {
  response: Response;
  json?: ErrorResponse;

  constructor(response: Response, json?: ErrorResponse) {
    super(`HTTP Error: ${response.status} ${response.statusText}`);
    this.response = response;
    this.json = json;
  }

  static async fromResponse(response: Response, json?: ErrorResponse) {
    if (!response.bodyUsed) {
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        json = await response.json();
      }
    }
    return new HTTPError(response, json);
  }

  // A 401/403/404 is a client auth-or-resource state — unauthenticated,
  // forbidden, or absent — not a fault worth reporting. A 401 in particular is
  // routine on the external API: an anonymous visitor, or a session that lapsed
  // while the tab was open, whose next request surfaces as a bare 401 rather
  // than going through the app's redirect-to-login handler. Callers that act on
  // a 401 (login gating, password prompts) still catch it; this only keeps it
  // out of error reporting.
  get _isNominal() {
    const status = this.response.status;
    return status == 401 || status == 403 || status == 404;
  }

  display() {
    if (this.json) {
      return shortErrorMessage(this.json);
    } else {
      return this.message;
    }
  }
}

/**
 * One-line, human-readable text for a caught value of unknown type. For an
 * `HTTPError` this is the server's own message; anything else falls back to its
 * string form.
 */
export function errorDescription(e: unknown): string {
  return e instanceof HTTPError ? e.display() : `${e}`;
}

type FieldError = {message?: string};

// Format our error responses as a 1-line human readable message.
// This might hide issues with other fields
function shortErrorMessage(obj: ErrorResponse) {
  if (obj.type_ === "validation_error") {
    for (const [_, errors] of Object.entries(obj.extra as object)) {
      return errors.map((e: FieldError) => e?.message || "").join("\n");
    }
  }

  return obj.error;
}
